import { Popover } from "antd";
import { HomeOutlined, CheckOutlined } from "@ant-design/icons";
import { useState } from "react";
import { FlatSection } from "./types";
import { SectionOptionLabel } from "./SectionOptionLabel";

interface MoveToMenuProps {
  currentParent: number | null;
  flatSections: FlatSection[];
  /** Section ids that cannot be chosen (self + descendants). */
  disallowed?: Set<number>;
  onMove: (target: number | null) => void;
  title: string;
  rootLabel: string;
  children: React.ReactNode;
}

/**
 * A "move to section" picker styled like an antd Select dropdown — clean rows
 * with hover, indentation per depth and a highlighted current location.
 */
export const MoveToMenu: React.FC<MoveToMenuProps> = ({
  currentParent,
  flatSections,
  disallowed,
  onMove,
  title,
  rootLabel,
  children,
}) => {
  const [open, setOpen] = useState(false);

  const rowClass =
    "w-full text-left bg-transparent border-0 appearance-none flex items-center justify-between gap-4 px-3 py-[5px] rounded text-sm cursor-pointer transition-colors hover:bg-black/[0.04] disabled:cursor-default";
  const currentClass =
    "!bg-[#e6f4ff] !text-[#1677ff] font-semibold hover:!bg-[#e6f4ff]";

  const select = (target: number | null) => {
    setOpen(false);
    onMove(target);
  };

  const content = (
    <div className="min-w-[240px] max-h-[320px] overflow-y-auto">
      <div className="px-3 py-1 text-xs text-gray-400">{title}</div>

      <button
        type="button"
        disabled={currentParent === null}
        className={`${rowClass} ${currentParent === null ? currentClass : ""}`}
        onClick={() => select(null)}
      >
        <span className="flex items-center">
          <HomeOutlined className="mr-1.5" />
          {rootLabel}
        </span>
        {currentParent === null && <CheckOutlined className="text-xs" />}
      </button>

      {flatSections
        .filter((s) => !disallowed?.has(s.id))
        .map((s) => {
          const isCurrent = s.id === currentParent;
          return (
            <button
              key={s.id}
              type="button"
              disabled={isCurrent}
              className={`${rowClass} ${isCurrent ? currentClass : ""}`}
              onClick={() => select(s.id)}
            >
              <SectionOptionLabel name={s.name} depth={s.depth} />
              {isCurrent && <CheckOutlined className="text-xs" />}
            </button>
          );
        })}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayInnerStyle={{ padding: 4 }}
    >
      {children}
    </Popover>
  );
};
